import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

const STORAGE_KEY = "prec-cookie-consent";
const VERSION = "1";

type Prefs = {
  version: string;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

function loadPrefs(): Prefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Prefs;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePrefs(prefs: Omit<Prefs, "version" | "necessary" | "timestamp">) {
  const full: Prefs = {
    version: VERSION,
    necessary: true,
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  window.dispatchEvent(new CustomEvent("prec:cookie-consent", { detail: full }));
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const prefsFocusRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = "cookie-banner-heading";

  useEffect(() => {
    const existing = loadPrefs();
    if (!existing) {
      setVisible(true);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    } else if (previouslyFocused.current) {
      previouslyFocused.current.focus?.();
      previouslyFocused.current = null;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const target = showPrefs ? prefsFocusRef.current : initialFocusRef.current;
    target?.focus();
  }, [visible, showPrefs]);

  const closeWithReject = useCallback(() => {
    savePrefs({ analytics: false, marketing: false });
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeWithReject();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, closeWithReject]);

  if (!visible) return null;

  const acceptAll = () => {
    savePrefs({ analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectAll = () => closeWithReject();

  const saveCustom = () => {
    savePrefs({ analytics, marketing });
    setVisible(false);
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={headingId}
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-black/95 p-5 text-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur sm:p-6">
        {!showPrefs ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">
                {t("cookies.eyebrow")}
              </p>
              <h2 id={headingId} className="mt-2 text-lg font-semibold">
                {t("cookies.title")}
              </h2>
              <p className="mt-2 text-sm text-white/70">
                <Trans
                  i18nKey="cookies.body"
                  components={[
                    <Link
                      key="0"
                      to="/privacy-policy"
                      className="text-lime-300 underline hover:text-lime-200"
                    />,
                  ]}
                />
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:shrink-0">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                className="whitespace-nowrap rounded-full border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
              >
                {t("cookies.preferences")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="whitespace-nowrap rounded-full border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
              >
                {t("cookies.rejectAll")}
              </button>
              <button
                ref={initialFocusRef}
                type="button"
                onClick={acceptAll}
                className="whitespace-nowrap rounded-full bg-lime-300 px-4 py-2 text-sm font-semibold text-black transition hover:bg-lime-200"
              >
                {t("cookies.acceptAll")}
              </button>
            </div>

          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">
                {t("cookies.prefsEyebrow")}
              </p>
              <h2 id={headingId} className="mt-2 text-lg font-semibold">
                {t("cookies.prefsTitle")}
              </h2>
            </div>
            <ul className="space-y-3">
              <li className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold">{t("cookies.necessary")}</p>
                    <p className="mt-1 text-xs text-white/60">{t("cookies.necessaryBody")}</p>
                  </div>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] uppercase tracking-wider text-white/70">
                    {t("cookies.always")}
                  </span>
                </div>
              </li>
              <li className="rounded-xl border border-white/10 bg-white/5 p-4">
                <label className="flex cursor-pointer items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold">{t("cookies.analytics")}</p>
                    <p className="mt-1 text-xs text-white/60">{t("cookies.analyticsBody")}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-lime-300"
                  />
                </label>
              </li>
              <li className="rounded-xl border border-white/10 bg-white/5 p-4">
                <label className="flex cursor-pointer items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold">{t("cookies.marketing")}</p>
                    <p className="mt-1 text-xs text-white/60">{t("cookies.marketingBody")}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-lime-300"
                  />
                </label>
              </li>
            </ul>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                ref={prefsFocusRef}
                type="button"
                onClick={() => setShowPrefs(false)}
                className="rounded-full border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
              >
                {t("cookies.back")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="rounded-full border border-white/20 px-4 py-2 text-sm text-white transition hover:bg-white/10"
              >
                {t("cookies.rejectAll")}
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="rounded-full bg-lime-300 px-4 py-2 text-sm font-semibold text-black transition hover:bg-lime-200"
              >
                {t("cookies.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
